import creditcardutils from 'creditcardutils';

/**
 * DV-2789 / DV-2832 (PCI-DSS 3.3/3.4): LogRocket records fetch/XHR request AND response bodies by
 * default. Cardholder data must never be captured into a session replay:
 *   - the card security code (CVV / `cardCode`) — submitted on card-create (`POST /customer/card`);
 *   - the full card number (PAN) — sent in the card-create request body, and (DV-2832) embedded in
 *     serialized values (e.g. an Authorize.net direct-response string) or carried as a number / an
 *     object key, where a key-based rule alone would miss it.
 *
 * Two complementary rules:
 *   1. Key-based — redact any `cardCode` / `cardNumber` key's value wherever it appears.
 *   2. Value-based (DV-2832) — redact any run of 13–19 digits (formatted or not) that
 *      `creditcardutils.validateCardNumber` recognises as a real card number (IIN + type length +
 *      Luhn). The IIN/type gate keeps order numbers, IMEIs and loyalty ids that merely pass Luhn.
 *
 * String bodies are scrubbed with regex on the raw text (NO `JSON.parse`/`stringify`), so 19-digit
 * ids aren't silently rounded on the round-trip and PANs are caught regardless of position
 * (value / number / object key / dash- or space-formatted / embedded after other digits). A
 * sensitive key that survives only in escaped form (JSON-in-JSON) drops the whole body. Plain-object
 * bodies are walked and
 * rebuilt in place. Uses only lookAHEAD — no lookbehind, which throws at parse time on older Safari
 * when built via `new RegExp` and would blank-page every user on those browsers.
 *
 * Deliberately URL-independent: LogRocket's response object doesn't reliably carry the request URL.
 * (DOM input capture is handled separately via `data-private` on the card inputs in CardFormModal.)
 */

const REDACTED = '[REDACTED]';

/** Card-data keys (lowercased) whose value must never reach a session replay. */
const SENSITIVE_KEYS = new Set(['cardcode', 'cardnumber']);

/** Fast-path probe: does a string mention a sensitive key at all? */
const SENSITIVE_KEY_PATTERN = /card(code|number)/i;

/**
 * A run of 13–19 digits tolerating single space/dash separators between them, ending at a non-digit
 * boundary (lookAHEAD only). This is a *candidate* finder; `isPan` decides via card validation.
 */
const PAN_CANDIDATE = /\d(?:[ -]?\d){12,18}(?!\d)/g;

/** A `"cardCode"` / `"cardNumber"` JSON property and its (string | number | null) value. */
const SENSITIVE_KEY_VALUE =
  /"(cardnumber|cardcode)"\s*:\s*("(?:[^"\\]|\\.)*"|-?\d+(?:\.\d+)?|true|false|null)/gi;

/**
 * Luhn (mod-10) check. Always required: creditcardutils marks UnionPay `luhn: false`, so its
 * `validateCardNumber` accepts any 62/88-prefixed 16–19-digit id with no checksum — gating on Luhn
 * here closes that false-positive class (and IMEI/loyalty ids that happen to hit a card IIN).
 */
function luhnValid(digits: string): boolean {
  let sum = 0;
  let double = false;
  for (let i = digits.length - 1; i >= 0; i--) {
    let d = digits.charCodeAt(i) - 48;
    if (d < 0 || d > 9) return false;
    if (double) {
      d *= 2;
      if (d > 9) d -= 9;
    }
    sum += d;
    double = !double;
  }
  return sum % 10 === 0;
}

/**
 * Fallback for the 17–19 digit lengths creditcardutils' stale (2015-era) table under-reports — it
 * caps Visa/Discover at 16, so Luhn-valid 19-digit PANs of those brands would otherwise escape.
 * Length-scoped to 17–19 on purpose: a 15-digit "4…" is an Amex-length IMEI, not a Visa, so this
 * does NOT reintroduce the IMEI false-positive (≤16-digit cards are covered by validateCardNumber).
 */
function hasExtendedLengthCardIin(digits: string): boolean {
  if (digits.length < 17 || digits.length > 19) return false;
  const two = Number(digits.slice(0, 2));
  return (
    digits[0] === '4' || // Visa (13/16/19)
    digits[0] === '6' || // Discover / Maestro / UnionPay (6x)
    two === 81 // UnionPay 81-series
  );
}

/**
 * A card number: 13–19 digits, Luhn-valid, AND either recognised by creditcardutils (IIN + type
 * length) or a known IIN at an extended length the stale table misses. Luhn is always required.
 */
function isPan(candidate: string): boolean {
  const digits = candidate.replace(/\D/g, '');
  if (digits.length < 13 || digits.length > 19) return false;
  if (!luhnValid(digits)) return false;
  return (
    creditcardutils.validateCardNumber(digits) ||
    hasExtendedLengthCardIin(digits)
  );
}

/**
 * Redact every card-number run (formatted or not) in a string. Manual scan (not `String.replace`)
 * so that when a candidate fails `isPan` we retry one character later: dropping the lookbehind (for
 * Safari) left no left boundary, so a PAN preceded by another digit — a zero-padded account field
 * `"04111111111111111"`, or a PAN concatenated onto a numeric field — would otherwise be consumed
 * as one over-long run, fail `isPan`, and be skipped over with the embedded PAN never re-tested.
 */
function redactPansInString(value: string): string {
  PAN_CANDIDATE.lastIndex = 0;
  let out = '';
  let copied = 0; // index in `value` up to which output is already built
  let m: RegExpExecArray | null;
  while ((m = PAN_CANDIDATE.exec(value)) !== null) {
    if (isPan(m[0])) {
      out += value.slice(copied, m.index) + REDACTED;
      copied = m.index + m[0].length;
      PAN_CANDIDATE.lastIndex = copied;
    } else {
      // retry from the next position so a PAN embedded after a leading digit isn't skipped
      PAN_CANDIDATE.lastIndex = m.index + 1;
    }
  }
  return copied === 0 ? value : out + value.slice(copied);
}

/** Redact the value of any `cardCode` / `cardNumber` JSON property in a raw JSON string. */
function redactSensitiveKeyValues(value: string): string {
  return value.replace(
    SENSITIVE_KEY_VALUE,
    (_full, key: string) => `"${key}":"${REDACTED}"`
  );
}

/** Cheap early-exit precheck: is there at least one run that validates as a card number? */
function hasPan(value: string): boolean {
  PAN_CANDIDATE.lastIndex = 0;
  let m: RegExpExecArray | null;
  while ((m = PAN_CANDIDATE.exec(value)) !== null) {
    if (isPan(m[0])) return true;
    // same left-boundary retry as redactPansInString, so the precheck can't fast-path a body
    // whose only PAN sits immediately after another digit.
    PAN_CANDIDATE.lastIndex = m.index + 1;
  }
  return false;
}

/** True only for plain objects/arrays — the shapes we can safely rebuild key-by-key. */
function isPlainContainer(value: unknown): boolean {
  if (Array.isArray(value)) return true;
  return (
    value !== null &&
    typeof value === 'object' &&
    Object.getPrototypeOf(value) === Object.prototype
  );
}

/**
 * Recursively redact an already-parsed body: sensitive keys (by name), PANs in string leaves, PANs
 * carried as a number leaf, and PANs used as an object key. Rebuilds in place — no re-stringify, so
 * numeric ids keep their exact value.
 */
function stripSensitive(value: unknown): unknown {
  if (typeof value === 'string') return redactPansInString(value);
  if (typeof value === 'number' && Number.isInteger(value)) {
    return isPan(String(value)) ? REDACTED : value;
  }
  if (Array.isArray(value)) return value.map(stripSensitive);
  if (isPlainContainer(value)) {
    const source = value as Record<string, unknown>;
    const result: Record<string, unknown> = {};
    for (const key of Object.keys(source)) {
      const safeKey = redactPansInString(key); // a PAN used as a property name
      result[safeKey] = SENSITIVE_KEYS.has(key.toLowerCase())
        ? REDACTED
        : stripSensitive(source[key]);
    }
    return result;
  }
  // File/Blob/FormData/Date/class instances — no enumerable own keys; leave intact.
  return value;
}

/** Redact card data from a captured body (string or plain object/array), leaving others as-is. */
function sanitizeBody(body: unknown): unknown {
  if (typeof body === 'string') {
    const hasKey = SENSITIVE_KEY_PATTERN.test(body);
    if (!hasKey && !hasPan(body)) return body; // fast-path: nothing sensitive

    // A body that names a card key but isn't JSON (e.g. `cardCode=123&x=y`) can't be scrubbed
    // structurally — drop it wholesale rather than risk leaking the value.
    if (hasKey) {
      let jsonOk = true;
      try {
        JSON.parse(body);
      } catch {
        jsonOk = false;
      }
      if (!jsonOk) return REDACTED;

      // A sensitive key in *escaped* form — JSON embedded as a string inside JSON (a webhook
      // envelope, a logged upstream response), e.g. `{"payload":"{\"cardCode\":\"123\"}"}`. The
      // scalar key/value regex only matches an unescaped `"cardCode":`, so it can't reach the CVV
      // here. Drop the body wholesale rather than leak it. (`\\+` tolerates double-escaped envelopes.)
      if (/\\+"(?:cardnumber|cardcode)\\+"/i.test(body)) return REDACTED;
    }

    // Regex on the raw text — no parse/stringify, so big-integer ids aren't rounded, and PANs are
    // caught whether they sit in a value, a number, an object key, or dash/space-formatted.
    const scrubbed = redactPansInString(redactSensitiveKeyValues(body));
    // The scalar key/value regex can't scrub a sensitive key whose value is an object/array
    // (`"cardCode":{…}`); drop the whole body rather than leak the subtree. Rare — our card-create
    // sends scalar values — but a strict no-leak guarantee vs an adversarial backend response.
    if (hasKey && /"(cardnumber|cardcode)"\s*:\s*[[{]/i.test(scrubbed))
      return REDACTED;
    return scrubbed;
  }
  if (isPlainContainer(body)) return stripSensitive(body); // isPlainContainer already covers arrays
  return body; // FormData / File / Blob / Date / class instances — leave the diagnostic body intact
}

type NetworkEntry = { body?: unknown };

// One implementation for both the request and response sanitizers so they can't drift apart.
// Mutates `entry.body` in place (and returns the same entry) — LogRocket hands us a throwaway
// entry object and expects it back, so in-place is intentional.
export function sanitizeNetworkEntry<T extends NetworkEntry>(entry: T): T {
  if ('body' in entry) entry.body = sanitizeBody(entry.body);
  return entry;
}
